import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Smartphone } from "@/lib/icons";
import { cn } from "@/lib/utils";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Check for iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      // Show iOS instructions after a delay
      const timer = setTimeout(() => {
        if (!window.matchMedia("(display-mode: standalone)").matches) {
          setShowPrompt(true);
        }
      }, 10000); // 10 seconds
      return () => clearTimeout(timer);
    }

    // Listen for beforeinstallprompt event
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 10 seconds of browsing
      setTimeout(() => {
        setShowPrompt(true);
      }, 10000);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setShowPrompt(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("pwa-install-dismissed", new Date().toISOString());
  };

  if (!showPrompt) return null;

  return (
    <div
      className={cn(
        "fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80",
        "bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-border",
        "p-4 z-50 animate-in slide-in-from-bottom-5"
      )}
      role="dialog"
      aria-labelledby="pwa-install-title"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Cerrar instalacion"
      >
        <X className="w-5 h-5 text-muted-foreground" />
      </button>

      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 id="pwa-install-title" className="font-display font-bold text-sm">
            Instala UGC Lab
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isIOS
              ? "Toca el boton compartir y 'Agregar a inicio'"
              : "Accede mas rapido desde tu pantalla de inicio"}
          </p>
        </div>
      </div>

      {!isIOS && deferredPrompt && (
        <Button
          onClick={handleInstall}
          className="w-full mt-3 rounded-xl"
          size="sm"
        >
          <Download className="w-4 h-4 mr-2" />
          Instalar App
        </Button>
      )}

      {isIOS && (
        <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-xs text-muted-foreground text-center">
            Toca{" "}
            <span className="inline-flex items-center justify-center w-5 h-5 bg-primary/10 rounded">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3 3h-2v6h-2V5H9l3-3zm-7 9v10h14V11h-2v8H7v-8H5z" />
              </svg>
            </span>{" "}
            y luego <strong>Agregar a inicio</strong>
          </p>
        </div>
      )}
    </div>
  );
}
